/**
 * The weight of the thing. There is one.
 *
 * There were five tiers once, and choosing between them was the first
 * decision the game asked for and the least interesting one it had: a player
 * picking Ember was choosing to be told a gentler lie about what the Night
 * is. The Night is a duration, not a dial — it gets harder for as long as you
 * last, and the only honest answer to "how hard is it" is "how long did you
 * stand". So the tier stays as a shape for the numbers the director reads,
 * and there is exactly one of it.
 *
 * These move the *budget curve*, the director's adaptation cap and player
 * income — never enemy stat inflation. A grunt is a grunt; there are simply
 * more of them as the hours go, and they are chosen against you more cleverly.
 */

export interface DifficultyDef {
  id: string;
  name: string;
  flavour: string;
  /** Multiplier on the wave threat budget. */
  budgetMul: number;
  /** How steeply the S-curve climbs. */
  curveMul: number;
  /** Fraction of budget the director may spend on hard counters. */
  adaptCap: number;
  /** Multiplier on all player resource production. */
  incomeMul: number;
  /** Multiplier on build-phase length. */
  buildTimeMul: number;
  /** Starting resource multiplier. */
  startMul: number;
}

export const DIFFICULTIES: DifficultyDef[] = [
  {
    id: 'endless',
    name: 'Endless',
    flavour: 'It is not a difficulty. It is a duration.',
    /**
     * The numbers, and why they are not the old Endless tier's.
     *
     * When Endless was one of five, it could afford to be cruel at minute one
     * — a player who wanted a gentler opening picked Ember. As the only tier
     * it is every player's first hour as well as their fifth, and the tuned
     * opening is the one the whole build order, the tutorial and eighty
     * balance tests were written against. So the economy stays at the
     * baseline and the cruelty lives entirely in the curve: the budget climbs
     * harder than it ever did, and the director may spend more of it
     * answering how you actually defend. It is not harder to start. It is
     * harder to keep standing, which is the promise the name makes.
     */
    budgetMul: 1,
    curveMul: 1.28,
    adaptCap: 0.55,
    incomeMul: 1,
    buildTimeMul: 1,
    startMul: 1,
  },
];

const BY_ID = new Map<string, DifficultyDef>();
for (const d of DIFFICULTIES) BY_ID.set(d.id, d);

export function difficulty(id: string): DifficultyDef {
  return BY_ID.get(id) ?? DIFFICULTIES[0];
}
